"use client";

import * as React from "react";

// Simple class merging utility since we don't have the cn utility
function cn(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface ShineBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Width of the border in pixels
   * @default 1
   */
  borderWidth?: number;
  /**
   * Duration of the animation in seconds
   * @default 14
   */
  duration?: number;
  /**
   * Color of the border, can be a single color or an array of colors
   * @default "#000000"
   */
  shineColor?: string | string[];
}

/**
 * Shine Border
 *
 * An animated background border effect component with configurable properties.
 */
export function ShineBorder({
  borderWidth = 1,
  duration = 14,
  shineColor = "#000000",
  className,
  style,
  ...props
}: ShineBorderProps) {
  React.useEffect(() => {
    // Add keyframes style to head if it doesn't exist
    if (!document.getElementById("shine-animation-style")) {
      const styleSheet = document.createElement("style");
      styleSheet.id = "shine-animation-style";
      styleSheet.textContent = `
        @keyframes shine {
          0% {
            background-position: 0% 0%;
          }
          25% {
            background-position: 100% 100%;
          }
          50% {
            background-position: 100% 0%;
          }
          75% {
            background-position: 0 100%;
          }
          100% {
            background-position: 0% 0%;
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  return (
    <div
      style={
        {
          "--border-width": `${borderWidth}px`,
          "--duration": `${duration}s`,
          backgroundImage: `radial-gradient(transparent,transparent, ${
            Array.isArray(shineColor) ? shineColor.join(",") : shineColor
          },transparent,transparent)`,
          backgroundSize: "300% 300%",
          mask: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
          WebkitMask: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: `${borderWidth}px`,
          animation: `shine var(--duration) infinite linear`,
          willChange: "background-position",
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          borderRadius: "inherit",
          ...style,
        } as React.CSSProperties
      }
      className={cn(className || "")}
      {...props}
    />
  );
}